import React, { useEffect, useState, useRef, useCallback  } from "react";
import { Modal, Button, Image } from "react-bootstrap";
import { FaPhone, FaRegCommentDots, FaListUl, FaArrowDown, FaArrowUp } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import moment from "moment";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import TemplateModal from "../whatsapp_messenger/TemplateModal";
import OutgoingCalls from "./OutgoingCalls";
import "react-toastify/dist/ReactToastify.css";

const formatUnix = (unixTime) => {
  if (!unixTime) return "Not Answered";
  return moment.unix(Number(unixTime)).format("DD MMM YYYY, hh:mm A");
};

const getDuration = (start, end) => {
  if (!start || !end) return "Not Answered";
  const duration = Number(end) - Number(start);
  const mins = Math.floor(duration / 60);
  const secs = duration % 60;
  return `${mins}m ${secs}s`;
};

const CallModal = ({ show, onHide, userDetail, selectedWhatsAppSetting, socket }) => {
  const [callLogs, setCallLogs] = useState([]);
  const [showInput, setShowInput] = useState(false);
  const [inputText, setInputText] = useState("");
  const [sending, setSending] = useState(false);
  const [showHideModal, setShowHideModal] = useState(false);
  const [inCall, setInCall] = useState(false);
  const [remoteStream, setRemoteStream] = useState(null);
  const [callData, setCallData] = useState(null);

  const localStreamRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const requestInProgressRef = useRef(false);
  const toastIdRef = useRef(null);

  const full_number = `${userDetail?.country_code || ""}${userDetail?.whatsapp_number || ""}`;

  useEffect(() => {
    if (show && userDetail?.whatsapp_number && selectedWhatsAppSetting) {
      fetchCallLogs();
    }
  }, [show, userDetail?.whatsapp_number, selectedWhatsAppSetting]);

  const fetchCallLogs = useCallback(async () => {
    try {
      const res = await WhatsAppAPI.getCallLogs(selectedWhatsAppSetting, userDetail?.full_number);
      setCallLogs(res.success ? res.records || [] : []);
    } catch (error) {
      console.error("Failed to fetch call logs:", error);
      setCallLogs([]);
    }
  }, [selectedWhatsAppSetting, userDetail?.full_number]);

  const fetchCallPermission = async () => {
    const res = await WhatsAppAPI.getCallPermission(
      selectedWhatsAppSetting,
      userDetail?.full_number
    );

    if (res.success) {
      // console.log("Call Permission Info:", res.meta_response);
      // Optionally, set state for UI control:
      // setCallPermission(res.meta_response.permission);
      // setAvailableActions(res.meta_response.actions);
    } else {
      console.warn("Failed to fetch call permission:", res.error);
    }
  };

  const renderIcon = (status) =>
    status === "Incoming" ? <FaArrowDown className="text-success me-2" /> : <FaArrowUp className="text-primary me-2" />;

  const initiateOutgoingCall = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;

      const pc = new RTCPeerConnection();
      peerConnectionRef.current = pc;

      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      pc.ontrack = (event) => {
        remoteAudioRef.current.srcObject = event.streams[0];
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const payload = {
        messaging_product: "whatsapp",
        to: userDetail?.full_number,
        action: "connect",
        session: {
          sdp_type: "offer",
          sdp: offer.sdp,
        },
      };

      const response = await WhatsAppAPI.acceptCall(payload, selectedWhatsAppSetting);

      if (!response?.success) {
        toast.error(response?.meta_response?.error?.message || "Call failed");
        return;
      }

      const call_id = response?.meta_response?.calls?.[0]?.id;
      if (!call_id) {
        toast.error("No call ID returned.");
        return;
      }

      setCallData({
        call_id,
        business_number: selectedWhatsAppSetting,
      });

      await WhatsAppAPI.insertCall({
        name: userDetail?.contactname || null,
        whatsapp_number: userDetail?.full_number,
        business_number: selectedWhatsAppSetting,
        status: "Outgoing",
        event: "connect",
        call_id: call_id,
        start_time: Math.floor(Date.now() / 1000),
        sdp: offer.sdp,
        sdp_type: "offer",
        direction: "BUSINESS_INITIATED",
      });

      setInCall(true);
    } catch (err) {
      console.error("Call initiation failed:", err);
      toast.error("Call initiation failed. Check console.");
    }
  };

  const sendCallPermissionRequest = useCallback(async (message) => {
    // Prevent multiple executions
    if (requestInProgressRef.current || !message?.trim()) {
      // console.log("Request blocked - already in progress or no message");
      return;
    }

    // Set flag to prevent multiple calls
    requestInProgressRef.current = true;
    setSending(true);

    // Clear any existing toast
    if (toastIdRef.current) {
      toast.dismiss(toastIdRef.current);
    }

    try {
      // console.log("Sending WhatsApp call permission message...");
      const reqBody = {
        messaging_product: "whatsapp",
        recipient_type: "individual",
        to: userDetail?.full_number,
        type: "interactive",
        interactive: {
          type: "call_permission_request",
          action: { name: "call_permission_request" },
          body: { text: message },
        },
      };

      const result = await WhatsAppAPI.sendSingleWhatsAppTextMessage(reqBody, selectedWhatsAppSetting);

      if (result?.error) {
        toastIdRef.current = toast.error(result?.error?.message || "Failed to send call permission request.");
      } else {
        toastIdRef.current = toast.success("Call permission request sent successfully");

        const messageId = result.messages?.[0]?.id;
        
        // Don't await this to prevent blocking the UI reset
        WhatsAppAPI.insertMsgHistoryRecords({
          parent_id: userDetail.id || null,
          name: userDetail?.contactname || "",
          message: message,
          whatsapp_number: userDetail?.full_number,
          status: "Outgoing",
          recordtypename: "lead",
          is_read: true,
          message_id: messageId,
          business_number: selectedWhatsAppSetting,
        }).then(() => {
          // Show toast for 2 seconds then close modal
          setTimeout(() => {
            onHide();
          }, 2000);
        }).catch(err => {
          console.error("Failed to insert message history:", err);
          // Still close the modal after 2 seconds even if insertion fails
          setTimeout(() => {
            onHide();
          }, 2000);
        });

        // Reset UI immediately
        setInputText("");
        setShowInput(false);
      }
    } catch (err) {
      console.error("Send call permission error:", err);
      toastIdRef.current = toast.error("Failed to send call permission.");
    } finally {
      // Always reset both state and ref
      setSending(false);
      requestInProgressRef.current = false;
    }
  }, [userDetail?.full_number, userDetail?.contactname, userDetail?.id, selectedWhatsAppSetting]);

  const handleRequestPermission = useCallback(() => {
    if (requestInProgressRef.current) {
      // console.log("Request permission blocked - already in progress");
      return;
    }
    
    setInputText("We would like to call you to help support on your query");
    setShowInput(true);
  }, []);

  const handleSendMessage = useCallback(() => {
    if (requestInProgressRef.current || !inputText.trim()) {
      // console.log("Send message blocked - already in progress or no text");
      return;
    }
    sendCallPermissionRequest(inputText);
  }, [inputText, sendCallPermissionRequest]);

  // Reset refs when modal closes
  useEffect(() => {
    if (!show) {
      requestInProgressRef.current = false;
      setSending(false);
      setShowInput(false);
      setInputText("");
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = null;
      }
    }
  }, [show]);

  return (
    <>
      <Modal show={show} onHide={onHide} centered size="md">
        <Modal.Header closeButton>
          <Modal.Title>Call History</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-3">
            <Image
              src={`https://ui-avatars.com/api/?name=${userDetail?.contactname || "U"}`}
              roundedCircle
              width={70}
              height={70}
            />
            <h5 className="mt-2">{userDetail?.contactname || "Unknown"}</h5>
            <small className="text-muted">{full_number}</small>

            <div className="d-flex justify-content-center gap-3 mt-3 flex-wrap">
              <Button variant="success" size="sm" onClick={initiateOutgoingCall}>
                <FaPhone className="me-1" /> Call Now
              </Button>

              <div className="text-center">
                <Button
                  variant="dark"
                  size="sm"
                  onClick={handleRequestPermission}
                  disabled={sending}
                >
                  <FaListUl className="me-1" /> Request Permission
                </Button>
              </div>
            </div>

            {showInput && (
              <div className="d-flex align-items-center gap-2 mt-3">
                <input
                  type="text"
                  className="form-control"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Enter your message"
                />
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSendMessage}
                  disabled={sending || !inputText.trim()}
                >
                  {sending ? "Sending..." : "Send"}
                </Button>
              </div>
            )}
          </div>

          <div style={{ 
            maxHeight: "300px", 
            overflowY: "auto",     
            scrollbarWidth: "none", 
            msOverflowStyle: "none" 
          }}>
            {callLogs.length > 0 ? (
              callLogs.map((call, idx) => (
                <div
                  key={`${call.call_id || call.id || idx}-${call.start_time || idx}`}
                  className="d-flex justify-content-between align-items-center border-bottom py-2"
                >
                  <div className="d-flex align-items-center">
                    {renderIcon(call.status)}
                    <div>
                      <div className="fw-semibold">{call.status}</div>
                      <small className="text-muted">{formatUnix(call.start_time)}</small>
                    </div>
                  </div>
                  <div className="text-end">
                    <small>
                      {call.end_time ? (
                        getDuration(call.start_time, call.end_time)
                      ) : (
                        <span className="text-danger">Not Answered</span>
                      )}
                    </small>
                  </div>
                </div>
              ))
            ) : (""
              // <div className="text-muted text-center">No call history found</div>
            )}
          </div>
          
          <div className="p-3 bg-light rounded shadow-sm small">
            <strong>ℹ️ WhatsApp Calling Guidelines:</strong>
            <ul className="mb-1 ps-3">
              <li>Users must grant call permission before you can place a call.</li>
              <li>Permission is temporary – valid for <strong>7 days</strong> after approval.</li>
              <li>You can only make <strong>5 connected calls per user per 24 hours.</strong></li>
              <li>Permission can be granted via:
                <ul className="ps-3">
                  <li>Interactive call request message</li>
                  <li>User calling you first (callback)</li>
                </ul>
              </li>
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Close</Button>
        </Modal.Footer>
      </Modal>

      {inCall && (
        <OutgoingCalls
          userDetail={userDetail}
          socket={socket}
          peerConnectionRef={peerConnectionRef}
          remoteAudioRef={remoteAudioRef}
          localStreamRef={localStreamRef}
          onEndUI={() => {
            setInCall(false);
            setCallData(null);
          }}
          callData={callData}
        />
      )}

      {showHideModal && (
        <TemplateModal
          show={showHideModal}
          onHide={() => setShowHideModal(false)}
          contactData={userDetail}
          refreshData={fetchCallLogs}
          filterData={{ recordType: "recentlyMessage" }}
          selectedWhatsAppSetting={selectedWhatsAppSetting}
        />
      )}

      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        limit={3}
        toastId="callModal"
      />
    </>
  );
};

export default CallModal;